import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box
} from '@mui/material';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'

import { useNavigate, Link } from 'react-router-dom';

import CommonBreadcumbs from '../common/CommonBreadcumbs';
import DataGridToolbar from '../common/DataGridToolbar';
import DataGridTable from '../common/DataGridTable';
import CommonRestricted from '../common/CommonRestricted';

import { icons } from '../common/CommonIcons';
import { useDataGridParams } from '../../hooks/useDataGridParams';
import { useSystemPrompt } from '../../hooks/useSystemPrompt';
import { useAuditTrails, auditTrailTypes, targetTypes } from '../../hooks/api/useAuditTrails';
import { useSystemRoutes, routeTypes } from '../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../hooks/usePermissions';

export default function ListAuditTrails({
    routeType
}) {

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useAuditTrails({ auditTrailType: auditTrailTypes.auditTrail, targetType: targetTypes.auditTrail });

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.audit.id, scopeTypes.audit.auditTrail.id)

    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const gridRef = useRef();

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {

            const result = await list(props)

            return {
                data: result?.data?.audit_trails,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }
    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {
        defineColumns([
            {
                name: 'target',
                header: 'Target',
            },
            {
                name: 'module',
                header: 'Module'
            },
            {
                name: 'trail_ref',
                header: 'Trail Ref',
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageView.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`settings/auditTrail/view/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'type',
                header: 'Type'
            },
            {
                name: 'description',
                header: 'Description'
            },
            {
                name: 'refer_id',
                header: 'Refer ID'
            },
            {
                name: 'tags',
                header: 'Tags',
                renderValue: ({ value, cellProps }) => {

                    let dataArr = []

                    value.forEach((i) => {
                        dataArr.push(
                            i.tag_label
                        )
                    })

                    return (
                        <>
                            {`${dataArr.join(", ")}`}
                        </>
                    )

                }
            },
        ])
    }

    const handleDelete = async () => {

        try {
            let result = await remove({ ids: selectedIds })

            promptMessage(true, result?.message)
            setRefreshDataGrid(currentVal => currentVal + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {
                        navigate(getRoute("settings/auditTrail/new"));
                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        // false,
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )
    }

    const getBreadcumbActionMenus = () => {

        let props = []

        if (isPermissionAllow({ scopeType: scopeTypes[scopeIds.audit.id].auditTrailTag.id, functionType: functionTypes.pageList.id })) {
            props.push({
                displayText: "Manage Tags",
                onClick: () => {

                    navigate(getRoute(`settings/auditTrail/tags`))
                }
            })
        }

        return props
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.audits })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle="Audit Trails"
                actionMenus={
                    getBreadcumbActionMenus()
                }
                breadcrumbs={[
                    {
                        displayText: "Audit Trails",
                        props: {
                            // underline: "hover",

                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}